// ── The business name, from the CRM's Coach Profile card ────────────────
//
// ⚠️ SAVED THE WAY THE COACH APP SAVES IT. coach-update-profile.js writes
// coaches.business_name and coaches.settings.businessName together, from one
// field. Agreements, the client app and the top of client emails each read one
// of those two. Until this card had the field, only the coach app's Edit
// Profile could change the name after signup.
//
// ⚠️ AND THE CRM'S OWN OLD COPY IS DROPPED. The CRM's settings
// (user_config.config.settings) can still hold a businessName from before;
// nothing writes it any more. Where agreements read the name, that copy
// outranks coaches.settings, and every CRM load copies it back over
// coaches.settings (coach-settings-sync.js). Left in place, it would undo this
// save on the next page load.

/**
 * The business name on file, in the order agreements read it: the CRM's own
 * settings, then coaches.settings, then the coaches row (the name typed at
 * signup).
 *
 * @param {Record<string, any>|null|undefined} localSettings the CRM's settings, as loadSettings() returns them
 * @param {Record<string, any>|null|undefined} coachSettings coaches.settings
 * @param {{ business_name?: string|null }|null|undefined} coachRow the coaches row
 * @returns {string} the name, trimmed, or '' when there is none
 */
export function businessNameOnFile(localSettings, coachSettings, coachRow) {
  const found = [localSettings?.businessName, coachSettings?.businessName, coachRow?.business_name]
    .find((v) => typeof v === 'string' && v.trim());
  return found ? found.trim() : '';
}

/**
 * Save the business name the way the coach app does, then drop the CRM's own
 * old copy so it cannot win over the new one. A failed save changes nothing.
 *
 * @param {string} typed what the coach typed
 * @param {{
 *   callFunction: (name: string, body: Record<string, any>) => Promise<any>,
 *   loadSettings: () => Record<string, any>,
 *   saveSettingsToStorage: (settings: Record<string, any>) => void,
 * }} deps the CRM's own helpers, passed in
 * @returns {Promise<string>} the name as saved
 */
export async function saveBusinessName(typed, { callFunction, loadSettings, saveSettingsToStorage }) {
  // Trimmed and capped at 80, as coach-update-profile.js stores it. Blank
  // clears it, and the coach's own name is used in its place.
  const businessName = String(typed ?? '').trim().slice(0, 80);
  await callFunction('coach-update-profile', { businessName });
  const local = loadSettings();
  if (local && Object.prototype.hasOwnProperty.call(local, 'businessName')) {
    delete local.businessName;
    saveSettingsToStorage(local);
  }
  return businessName;
}
